import { useEffect, useRef, useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 150;

interface Props {
	content: React.ReactNode;
	/** Classes for the popover panel, e.g. its width */
	className?: string;
	/** Classes for the hovered wrapper around `children` */
	anchorClassName?: string;
	align?: "start" | "center" | "end";
	children: React.ReactNode;
}

/**
 * A popover that opens after hovering `children` for a moment and stays open
 * while the pointer is over it, so links inside can be reached.
 */
const HoverPopover = ({ content, className, anchorClassName, align = "start", children }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout>>();

	const schedule = (open: boolean) => {
		clearTimeout(timer.current);
		timer.current = setTimeout(() => setIsOpen(open), open ? OPEN_DELAY_MS : CLOSE_DELAY_MS);
	};
	useEffect(() => () => clearTimeout(timer.current), []);

	const hoverProps = { onMouseEnter: () => schedule(true), onMouseLeave: () => schedule(false) };

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverAnchor asChild>
				<span className={cn("block min-w-0", anchorClassName)} {...hoverProps}>
					{children}
				</span>
			</PopoverAnchor>
			<PopoverContent
				side="bottom"
				align={align}
				className={cn("!p-3", className)}
				// Hovering must not move focus away from the schedule
				onOpenAutoFocus={(e) => e.preventDefault()}
				{...hoverProps}
			>
				{content}
			</PopoverContent>
		</Popover>
	);
};

export default HoverPopover;
